import { Crosshair, UserRound } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  cn,
  colorForUser,
  floorUnmapped,
  formatLevel,
  relativeTime,
  SOURCE_LABEL,
  sourceKind,
} from '@/lib/utils'
import type { LiveUser } from '@/types/liveview'

interface UserListProps {
  users: LiveUser[]
  selectedUserId: string | null
  currentLevel: number | null
  now: number
  /** A search filter is active, so "nothing here" means something different. */
  filtered: boolean
  onSelect: (userId: string) => void
  onLocate: (user: LiveUser) => void
}

export function UserList({
  users,
  selectedUserId,
  currentLevel,
  now,
  filtered,
  onSelect,
  onLocate,
}: UserListProps) {
  if (users.length === 0) {
    return (
      <div className="text-muted-foreground flex flex-1 flex-col items-center gap-2 px-4 py-10 text-center text-sm">
        <UserRound className="size-6 opacity-40" />
        {filtered ? (
          <p>No device matches that filter.</p>
        ) : (
          <>
            <p>No positions yet.</p>
            <p className="text-xs">
              Waiting for the SDK to <code className="text-foreground/70">POST /v1/positions</code>.
            </p>
          </>
        )}
      </div>
    )
  }

  return (
    // Sized by its content: the sidebar body scrolls as one column, and a
    // `flex-1 min-h-0` list inside it would shrink to nothing instead.
    <ScrollArea className="shrink-0">
      <ul className="flex flex-col gap-0.5 p-2">
        {users.map((user) => {
          const selected = user.user_id === selectedUserId
          const otherFloor =
            currentLevel !== null &&
            user.level !== null &&
            user.level !== undefined &&
            Math.abs(user.level - currentLevel) > 1e-9
          const kind = sourceKind(user.source)
          const unknownFloor = floorUnmapped(user.metadata)

          return (
            <li key={user.user_id}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => onSelect(user.user_id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onSelect(user.user_id)
                  }
                }}
                className={cn(
                  'group flex w-full cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors',
                  'hover:bg-accent focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]',
                  selected && 'bg-accent',
                )}
              >
                <span className="relative flex size-2.5 shrink-0 items-center justify-center">
                  {user.online && (
                    <span
                      className="liveview-pulse absolute size-2.5 rounded-full"
                      style={{ backgroundColor: colorForUser(user.user_id, user.source) }}
                    />
                  )}
                  <span
                    className={cn('size-2.5 rounded-full', !user.online && 'opacity-40')}
                    style={{ backgroundColor: colorForUser(user.user_id, user.source) }}
                  />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {user.name ?? user.user_id}
                    </span>
                    {kind !== 'phone' && (
                      <Badge variant="outline" className="shrink-0 px-1 py-0 text-[10px]">
                        {SOURCE_LABEL[kind]}
                      </Badge>
                    )}
                    {unknownFloor ? (
                      <Badge variant="warning" className="shrink-0 px-1 py-0 text-[10px]">
                        floor unknown
                      </Badge>
                    ) : (
                      otherFloor && (
                        <Badge variant="outline" className="shrink-0 px-1 py-0 text-[10px]">
                          L{formatLevel(user.level)}
                        </Badge>
                      )
                    )}
                  </div>
                  <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <span className="tabular-nums">{relativeTime(user.timestamp, now)}</span>
                    {typeof user.accuracy === 'number' && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="tabular-nums">±{user.accuracy.toFixed(1)} m</span>
                      </>
                    )}
                    {user.source && (
                      <>
                        <span className="opacity-40">·</span>
                        <span className="truncate">{user.source}</span>
                      </>
                    )}
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                  aria-label={`Centre map on ${user.name ?? user.user_id}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    onLocate(user)
                  }}
                >
                  <Crosshair />
                </Button>
              </div>
            </li>
          )
        })}
      </ul>
    </ScrollArea>
  )
}
